import React, { useEffect, useRef, useState } from 'react';
import './Runway.css';

const MOTION_TIMELINE = [
  { name: 'approach', duration: 5800 },
  { name: 'flare', duration: 1300 },
  { name: 'touchdown', duration: 900 },
  { name: 'rollout', duration: 3800 },
  { name: 'landingExit', duration: 2025 },
  { name: 'transition', duration: 500 },
  { name: 'taxiIn', duration: 1700 },
  { name: 'lineUp', duration: 900 },
  { name: 'takeoffRoll', duration: 5000 },
  { name: 'rotate', duration: 1300 },
  { name: 'climb', duration: 2200 },
  { name: 'departure', duration: 1700 },
  { name: 'reset', duration: 900 },
];

const LANDING_RUNWAY_PHASES = ['approach', 'flare', 'touchdown', 'rollout'];
const TAKEOFF_RUNWAY_PHASES = ['takeoffRoll', 'rotate', 'climb'];

const TOTAL_MOTION_DURATION = MOTION_TIMELINE.reduce(
  (total, segment) => total + segment.duration,
  0
);

const getPhaseRange = (phaseNames) => {
  const phaseSet = new Set(phaseNames);
  let start = null;
  let duration = 0;
  let cursor = 0;

  for (const segment of MOTION_TIMELINE) {
    if (phaseSet.has(segment.name)) {
      if (start === null) {
        start = cursor;
      }
      duration += segment.duration;
    }
    cursor += segment.duration;
  }

  return {
    start: start ?? 0,
    duration,
  };
};

const LANDING_RUNWAY_RANGE = getPhaseRange(LANDING_RUNWAY_PHASES);
const TAKEOFF_RUNWAY_RANGE = getPhaseRange(TAKEOFF_RUNWAY_PHASES);

const DEFAULT_SCENE_STATE = {
  phase: 'hidden',
  visible: false,
  x: 0,
  y: 0,
  scale: 0.82,
  heading: 0,
  pitch: 0,
  roll: 0,
  opacity: 0,
  altitude: 0,
  speed: 0,
  thrust: 0,
  lightOpacity: 0,
  heatOpacity: 0,
  bodyX: 0,
  bodyY: 0,
  shadowX: 0,
  shadowY: 0,
  shadowScale: 0.8,
  shadowOpacity: 0,
  shadowBlur: 12,
  shadowStretch: 1,
  smokeX: 0,
  smokeY: 0,
  smokeScale: 0.8,
  smokeOpacity: 0,
  smokeRotation: 0,
  smokeStretch: 1,
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const lerp = (start, end, progress) => start + (end - start) * progress;
const easeInCubic = (value) => value * value * value;
const easeOutCubic = (value) => 1 - Math.pow(1 - value, 3);
const easeInOutCubic = (value) =>
  value < 0.5 ? 4 * value * value * value : 1 - Math.pow(-2 * value + 2, 3) / 2;
const easeOutQuart = (value) => 1 - Math.pow(1 - value, 4);
const radiansToDegrees = (value) => (value * 180) / Math.PI;

const sampleBezierPoint = (p0, p1, p2, p3, t) => {
  const inv = 1 - t;
  const inv2 = inv * inv;
  const inv3 = inv2 * inv;
  const t2 = t * t;
  const t3 = t2 * t;

  return {
    x: inv3 * p0.x + 3 * inv2 * t * p1.x + 3 * inv * t2 * p2.x + t3 * p3.x,
    y: inv3 * p0.y + 3 * inv2 * t * p1.y + 3 * inv * t2 * p2.y + t3 * p3.y,
  };
};

const sampleBezierTangent = (p0, p1, p2, p3, t) => {
  const inv = 1 - t;
  const inv2 = inv * inv;
  const t2 = t * t;

  return {
    x:
      3 * inv2 * (p1.x - p0.x) +
      6 * inv * t * (p2.x - p1.x) +
      3 * t2 * (p3.x - p2.x),
    y:
      3 * inv2 * (p1.y - p0.y) +
      6 * inv * t * (p2.y - p1.y) +
      3 * t2 * (p3.y - p2.y),
  };
};

const buildBezierArcTable = (p0, p1, p2, p3, samples = 120) => {
  const lookup = [{ t: 0, length: 0 }];
  let totalLength = 0;
  let previousPoint = sampleBezierPoint(p0, p1, p2, p3, 0);

  for (let index = 1; index <= samples; index += 1) {
    const t = index / samples;
    const point = sampleBezierPoint(p0, p1, p2, p3, t);
    totalLength += Math.hypot(point.x - previousPoint.x, point.y - previousPoint.y);
    lookup.push({ t, length: totalLength });
    previousPoint = point;
  }

  return {
    lookup,
    totalLength,
  };
};

const getBezierTAtArcProgress = (arcTable, progress) => {
  if (!arcTable || arcTable.totalLength <= 0) {
    return clamp(progress, 0, 1);
  }

  const targetLength = arcTable.totalLength * clamp(progress, 0, 1);
  const { lookup } = arcTable;

  for (let index = 1; index < lookup.length; index += 1) {
    const previous = lookup[index - 1];
    const current = lookup[index];

    if (targetLength <= current.length) {
      const localSpan = Math.max(current.length - previous.length, 0.0001);
      const localProgress = (targetLength - previous.length) / localSpan;
      return lerp(previous.t, current.t, localProgress);
    }
  }

  return 1;
};

const getHeadingFromVector = (vx, vy, fallback = 0) => {
  if (Math.abs(vx) < 0.0001 && Math.abs(vy) < 0.0001) {
    return fallback;
  }

  return radiansToDegrees(Math.atan2(vx, -vy));
};

const normalizeHeadingNear = (angle, anchor) => {
  let normalized = angle;

  while (normalized - anchor > 180) {
    normalized -= 360;
  }

  while (normalized - anchor < -180) {
    normalized += 360;
  }

  return normalized;
};

const resolveTimelineSegment = (elapsed) => {
  let cursor = 0;

  for (const segment of MOTION_TIMELINE) {
    const segmentEnd = cursor + segment.duration;
    if (elapsed < segmentEnd) {
      return {
        name: segment.name,
        progress: (elapsed - cursor) / segment.duration,
        duration: segment.duration,
      };
    }
    cursor = segmentEnd;
  }

  const fallback = MOTION_TIMELINE[MOTION_TIMELINE.length - 1];
  return { name: fallback.name, progress: 1, duration: fallback.duration };
};

const measureRunwayScene = (container, runway) => {
  if (!container || !runway) {
    return null;
  }

  const containerRect = container.getBoundingClientRect();
  const runwayRect = runway.getBoundingClientRect();
  const runwayTop = runwayRect.top - containerRect.top;
  const runwayBottom = runwayRect.bottom - containerRect.top;
  const visibleRunwayTop = clamp(runwayTop, 0, containerRect.height);
  const visibleRunwayBottom = clamp(runwayBottom, 0, containerRect.height);
  const visibleRunwayHeight = Math.max(visibleRunwayBottom - visibleRunwayTop, 1);

  return {
    containerHeight: containerRect.height,
    runwayCenterX: runwayRect.left - containerRect.left + runwayRect.width / 2,
    runwayWidth: runwayRect.width,
    visibleRunwayTop,
    visibleRunwayBottom,
    visibleRunwayHeight,
  };
};

const composeSceneState = (metrics, values) => {
  const phase = values.phase || 'hidden';
  const visible = Boolean(values.visible);
  const opacity = visible ? values.opacity ?? 1 : 0;
  const altitude = clamp(values.altitude ?? 0, 0, 1.25);
  const y = values.y ?? metrics.visibleRunwayTop;
  const x = values.x ?? metrics.runwayCenterX;
  const perspectiveTop = metrics.visibleRunwayTop - metrics.containerHeight * 0.16;
  const perspectiveSpan = Math.max(metrics.visibleRunwayBottom - perspectiveTop, 1);
  const groundProgress = clamp(
    (y - perspectiveTop) / perspectiveSpan,
    0,
    1
  );
  const perspectiveScale = lerp(0.81, 1.07, groundProgress);
  const scale =
    (values.baseScale ?? 0.9) * perspectiveScale * (1 - Math.min(altitude, 1) * 0.085);
  const shadowTop = metrics.visibleRunwayTop - metrics.containerHeight * 0.035;
  const shadowY = clamp(
    values.shadowY ??
      lerp(y, metrics.visibleRunwayTop + metrics.visibleRunwayHeight * 0.18, altitude * 0.52),
    shadowTop,
    metrics.visibleRunwayBottom - 8
  );
  const shadowX = values.shadowX ?? lerp(x, metrics.runwayCenterX, altitude * 0.4);
  const shadowStretch = values.shadowStretch ?? lerp(0.98, 1.16, Math.min(altitude, 1));
  const smokeX = values.smokeX ?? x;
  const smokeY = values.smokeY ?? y + 16;

  return {
    phase,
    visible,
    x,
    y,
    scale,
    heading: values.heading ?? 0,
    pitch: values.pitch ?? 0,
    roll: values.roll ?? 0,
    opacity,
    altitude,
    speed: values.speed ?? 0,
    thrust: values.thrust ?? 0,
    lightOpacity: (values.lightOpacity ?? 0) * opacity,
    heatOpacity: (values.heatOpacity ?? 0) * opacity,
    bodyX: values.bodyX ?? 0,
    bodyY: values.bodyY ?? 0,
    shadowX,
    shadowY,
    shadowScale: values.shadowScale ?? scale * (0.78 - altitude * 0.1),
    shadowOpacity: visible
      ? (values.shadowOpacity ??
          clamp(0.12 + (1 - Math.min(altitude, 1)) * 0.27, 0, 0.42)) * opacity
      : 0,
    shadowBlur: values.shadowBlur ?? lerp(4, 18, Math.min(altitude, 1)),
    shadowStretch,
    smokeX,
    smokeY,
    smokeScale: values.smokeScale ?? 0.85,
    smokeOpacity: visible ? (values.smokeOpacity ?? 0) * opacity : 0,
    smokeRotation: values.smokeRotation ?? 0,
    smokeStretch: values.smokeStretch ?? 1,
  };
};

const addMotionTexture = (state, elapsed) => {
  if (!state.visible) {
    return state;
  }

  const seconds = elapsed / 1000;
  const smokePulse = state.smokeOpacity > 0 ? Math.sin(seconds * 6.2) * 0.02 : 0;

  return {
    ...state,
    bodyX: 0,
    bodyY: 0,
    smokeRotation: state.smokeRotation + Math.sin(seconds * 3.4) * 4,
    smokeScale: state.smokeScale * (1 + smokePulse),
  };
};

const getRangeProgress = (elapsed, range) =>
  clamp((elapsed - range.start) / Math.max(range.duration, 1), 0, 1);

const getLinearDecelToConstantProfile = (
  progress,
  constantStartDistance,
  startToHoldSpeedRatio = 3.4
) => {
  const s1 = clamp(constantStartDistance, 0.08, 0.98);
  const r = Math.max(startToHoldSpeedRatio, 1.01);
  const switchProgress = clamp(
    (2 * s1) / ((r + 1) - s1 * (r - 1)),
    0.0001,
    0.9999
  );
  const holdSpeed = 1 / (1 + (switchProgress * (r - 1)) / 2);
  const startSpeed = holdSpeed * r;

  if (progress <= switchProgress) {
    const decel = (holdSpeed - startSpeed) / switchProgress;
    return {
      distance: startSpeed * progress + 0.5 * decel * progress * progress,
      speed: startSpeed + decel * progress,
      holdSpeed,
    };
  }

  return {
    distance: s1 + holdSpeed * (progress - switchProgress),
    speed: holdSpeed,
    holdSpeed,
  };
};

const getQuadraticAccelToConstantProfile = (progress, maxSpeedDistance) => {
  const s1 = clamp(maxSpeedDistance, 0.08, 0.95);
  const startSpeedRatio = 0.04;
  const switchProgress = clamp(
    (3 * s1) / (1 + 2 * startSpeedRatio + 2 * s1 * (1 - startSpeedRatio)),
    0.0001,
    0.9999
  );
  const maxSpeed = 1 / (1 - (2 * (1 - startSpeedRatio) * switchProgress) / 3);
  const startSpeed = maxSpeed * startSpeedRatio;

  if (progress <= switchProgress) {
    const normalized = progress / switchProgress;
    const normalizedSquared = normalized * normalized;
    return {
      distance:
        maxSpeed *
        (startSpeedRatio * progress +
          ((1 - startSpeedRatio) * progress * progress * progress) /
            (3 * switchProgress * switchProgress)),
      speed: startSpeed + (maxSpeed - startSpeed) * normalizedSquared,
      maxSpeed,
      startSpeed,
    };
  }

  return {
    distance:
      (switchProgress * maxSpeed * (1 + 2 * startSpeedRatio)) / 3 +
      maxSpeed * (progress - switchProgress),
    speed: maxSpeed,
    maxSpeed,
    startSpeed,
  };
};

const getParkedSceneState = (metrics) =>
  composeSceneState(metrics, {
    phase: 'parked',
    visible: true,
    x: metrics.runwayCenterX,
    y: metrics.visibleRunwayBottom - metrics.visibleRunwayHeight * 0.18,
    baseScale: 0.92,
    heading: 0,
    pitch: 0,
    roll: 0,
    altitude: 0,
    speed: 0,
    thrust: 0.08,
    lightOpacity: 0.25,
    heatOpacity: 0,
    shadowOpacity: 0.36,
    shadowBlur: 4,
    shadowStretch: 0.96,
  });

const buildRunwaySceneGeometry = (metrics, cycleIndex = 0) => {
  const top = metrics.visibleRunwayTop;
  const bottom = metrics.visibleRunwayBottom;
  const height = metrics.visibleRunwayHeight;
  const centerX = metrics.runwayCenterX;
  const width = metrics.runwayWidth;
  const landingExitSide = cycleIndex % 2 === 0 ? -1 : 1;
  const taxiInSide = -landingExitSide;
  const departureSide = landingExitSide;
  const towardCenter = -taxiInSide;
  const approachStartY = top - metrics.containerHeight * 0.16;
  const touchdownY = top + height * 0.175;
  const runwayEndY = bottom - height * 0.075;
  const taxiEntryY = bottom - height * 0.075;
  const taxiEntryStartX = centerX + taxiInSide * width * 0.48;
  const landingExitEndY = taxiEntryY + 8;
  const landingExitEndX = taxiEntryStartX;
  const landingExitTurnSide = Math.sign(landingExitEndX - centerX) || taxiInSide;
  const takeoffStartY = bottom - height * 0.088;
  const liftoffY = top + height * 0.13;
  const climbEndY = top - metrics.containerHeight * 0.03;
  const departureEndY = top - metrics.containerHeight * 0.36;
  const departureEndX = centerX + departureSide * width * 0.24;
  const landingConstantSpeedStartY = top + height * 0.75;
  const landingConstantSpeedDistance = clamp(
    (landingConstantSpeedStartY - approachStartY) / Math.max(runwayEndY - approachStartY, 1),
    0.08,
    0.98
  );
  const takeoffRunwayExitDistance = clamp(
    (takeoffStartY - liftoffY) / Math.max(takeoffStartY - climbEndY, 1),
    0.08,
    0.95
  );
  const landingExitPath = {
    p0: { x: centerX, y: runwayEndY },
    p1: {
      x: centerX,
      y: runwayEndY + height * 0.06,
    },
    p2: {
      x: landingExitEndX - landingExitTurnSide * width * 0.16,
      y: landingExitEndY + height * 0.02,
    },
    p3: { x: landingExitEndX, y: landingExitEndY },
  };
  const landingExitArcTable = buildBezierArcTable(
    landingExitPath.p0,
    landingExitPath.p1,
    landingExitPath.p2,
    landingExitPath.p3
  );
  const taxiInPath = {
    p0: { x: taxiEntryStartX, y: taxiEntryY + 8 },
    p1: {
      x: taxiEntryStartX + towardCenter * width * 0.24,
      y: taxiEntryY + 8,
    },
    p2: { x: centerX, y: taxiEntryY + height * 0.08 },
    p3: { x: centerX, y: taxiEntryY },
  };
  const taxiInArcTable = buildBezierArcTable(
    taxiInPath.p0,
    taxiInPath.p1,
    taxiInPath.p2,
    taxiInPath.p3
  );
  const departurePath = {
    p0: { x: centerX, y: climbEndY },
    p1: { x: centerX, y: climbEndY - metrics.containerHeight * 0.085 },
    p2: {
      x: departureEndX - departureSide * width * 0.09,
      y: departureEndY + metrics.containerHeight * 0.045,
    },
    p3: { x: departureEndX, y: departureEndY },
  };
  const departureArcTable = buildBezierArcTable(
    departurePath.p0,
    departurePath.p1,
    departurePath.p2,
    departurePath.p3
  );
  const landingHoldProfile = getLinearDecelToConstantProfile(1, landingConstantSpeedDistance);
  const landingRunwayDistance = Math.max(runwayEndY - approachStartY, 1);
  const landingTaxiSpeed =
    (landingRunwayDistance * landingHoldProfile.holdSpeed) /
    Math.max(LANDING_RUNWAY_RANGE.duration, 1);
  const landingExitMotionDuration =
    landingTaxiSpeed > 0.0001
      ? landingExitArcTable.totalLength / landingTaxiSpeed
      : MOTION_TIMELINE.find((segment) => segment.name === 'landingExit')?.duration ?? 0;

  return {
    top,
    bottom,
    height,
    centerX,
    width,
    landingExitSide,
    taxiInSide,
    departureSide,
    towardCenter,
    approachStartY,
    touchdownY,
    runwayEndY,
    taxiEntryY,
    taxiEntryStartX,
    landingExitEndY,
    landingExitEndX,
    landingExitTurnSide,
    takeoffStartY,
    liftoffY,
    climbEndY,
    departureEndY,
    departureEndX,
    landingConstantSpeedDistance,
    takeoffRunwayExitDistance,
    landingExitPath,
    landingExitArcTable,
    taxiInPath,
    taxiInArcTable,
    departurePath,
    departureArcTable,
    landingTaxiSpeed,
    landingExitMotionDuration,
  };
};

const getRunwaySceneState = (elapsed, metrics, sceneGeometry) => {
  const segment = resolveTimelineSegment(elapsed);
  const {
    centerX,
    departureSide,
    approachStartY,
    touchdownY,
    runwayEndY,
    taxiEntryY,
    landingExitTurnSide,
    takeoffStartY,
    climbEndY,
    landingConstantSpeedDistance,
    takeoffRunwayExitDistance,
    landingExitPath,
    landingExitArcTable,
    taxiInPath,
    taxiInArcTable,
    departurePath,
    departureArcTable,
    landingExitMotionDuration,
  } = sceneGeometry;
  const landingRunwayProgress = getRangeProgress(elapsed, LANDING_RUNWAY_RANGE);
  const takeoffRunwayProgress = getRangeProgress(elapsed, TAKEOFF_RUNWAY_RANGE);
  const landingRunwayProfile = getLinearDecelToConstantProfile(
    landingRunwayProgress,
    landingConstantSpeedDistance
  );
  const takeoffRunwayProfile = getQuadraticAccelToConstantProfile(
    takeoffRunwayProgress,
    takeoffRunwayExitDistance
  );
  const landingRunwayY = lerp(approachStartY, runwayEndY, landingRunwayProfile.distance);
  const takeoffRunwayY = lerp(takeoffStartY, climbEndY, takeoffRunwayProfile.distance);

  switch (segment.name) {
    case 'approach': {
      return addMotionTexture(
        composeSceneState(metrics, {
          phase: 'approach',
          visible: true,
          x: centerX,
          y: landingRunwayY,
          baseScale: lerp(0.76, 0.86, segment.progress),
          heading: 180,
          pitch: lerp(1.8, 1.3, segment.progress),
          roll: 0,
          altitude: lerp(1.06, 0.18, easeOutQuart(segment.progress)),
          speed: landingRunwayProfile.speed,
          thrust: 0.18,
          lightOpacity: lerp(0.9, 1, segment.progress),
          heatOpacity: 0.06,
          shadowStretch: lerp(1.22, 1.06, segment.progress),
        }),
        elapsed
      );
    }

    case 'flare': {
      const flarePitch =
        segment.progress < 0.55
          ? lerp(1.9, 8.4, segment.progress / 0.55)
          : lerp(8.4, 6.2, (segment.progress - 0.55) / 0.45);

      return addMotionTexture(
        composeSceneState(metrics, {
          phase: 'flare',
          visible: true,
          x: centerX,
          y: landingRunwayY,
          baseScale: lerp(0.86, 0.88, segment.progress),
          heading: 180,
          pitch: flarePitch,
          roll: 0,
          altitude: lerp(0.18, 0.04, easeOutCubic(segment.progress)),
          speed: landingRunwayProfile.speed,
          thrust: 0.14,
          lightOpacity: 1,
          heatOpacity: 0.05,
          shadowStretch: lerp(1.08, 1.03, segment.progress),
        }),
        elapsed
      );
    }

    case 'touchdown': {
      const smokeBurst = Math.sin(segment.progress * Math.PI);

      return addMotionTexture(
        composeSceneState(metrics, {
          phase: 'touchdown',
          visible: true,
          x: centerX,
          y: landingRunwayY,
          baseScale: 0.89,
          heading: 180,
          pitch: lerp(6.2, 1.4, easeOutCubic(segment.progress)),
          roll: 0,
          altitude: lerp(0.05, 0, easeOutCubic(segment.progress)),
          speed: landingRunwayProfile.speed,
          thrust: 0.08,
          lightOpacity: lerp(1, 0.58, segment.progress),
          heatOpacity: 0.08,
          smokeX: centerX,
          smokeY: touchdownY + 18,
          smokeScale: lerp(0.64, 1.55, easeOutCubic(segment.progress)),
          smokeOpacity: smokeBurst * 0.72 * (1 - segment.progress * 0.18),
          smokeRotation: lerp(-2, 8, segment.progress),
          smokeStretch: lerp(1.02, 1.28, segment.progress),
          shadowOpacity: 0.42,
          shadowBlur: 4,
          shadowStretch: 1.02,
        }),
        elapsed
      );
    }

    case 'rollout': {
      const residualSmoke = segment.progress < 0.16 ? 1 - segment.progress / 0.16 : 0;
      const pitch =
        segment.progress < 0.25
          ? lerp(1.4, 0.2, segment.progress / 0.25)
          : lerp(0.2, 0, (segment.progress - 0.25) / 0.75);

      return addMotionTexture(
        composeSceneState(metrics, {
          phase: 'rollout',
          visible: true,
          x: centerX,
          y: landingRunwayY,
          baseScale: 0.9,
          heading: 180,
          pitch,
          roll: 0,
          altitude: 0,
          speed: landingRunwayProfile.speed,
          thrust: 0.04,
          lightOpacity: lerp(0.58, 0.18, segment.progress),
          heatOpacity: 0.03,
          smokeX: centerX,
          smokeY: lerp(touchdownY + 18, touchdownY + 28, segment.progress),
          smokeScale: lerp(1.34, 1.68, segment.progress),
          smokeOpacity: residualSmoke * 0.18,
          smokeStretch: lerp(1.2, 1.1, segment.progress),
          shadowOpacity: 0.4,
          shadowBlur: 4,
          shadowStretch: lerp(1.02, 0.97, segment.progress),
        }),
        elapsed
      );
    }

    case 'landingExit': {
      const exitProgress = clamp(
        (segment.progress * segment.duration) / Math.max(landingExitMotionDuration, 1),
        0,
        1
      );
      const exitT = getBezierTAtArcProgress(landingExitArcTable, exitProgress);
      const exitPoint = sampleBezierPoint(
        landingExitPath.p0,
        landingExitPath.p1,
        landingExitPath.p2,
        landingExitPath.p3,
        exitT
      );
      const exitTangent = sampleBezierTangent(
        landingExitPath.p0,
        landingExitPath.p1,
        landingExitPath.p2,
        landingExitPath.p3,
        exitT
      );
      const exitHeading = normalizeHeadingNear(
        getHeadingFromVector(exitTangent.x, exitTangent.y, 180),
        180
      );
      const exitCompletionProgress = clamp(
        landingExitMotionDuration / Math.max(segment.duration, 1),
        0.62,
        0.96
      );
      const fadeStart = clamp(exitCompletionProgress - 0.18, 0.5, 0.82);
      const fadeOut = clamp((segment.progress - fadeStart) / Math.max(1 - fadeStart, 0.0001), 0, 1);

      return addMotionTexture(
        composeSceneState(metrics, {
          phase: 'landing-exit',
          visible: true,
          x: exitPoint.x,
          y: exitPoint.y,
          baseScale: lerp(0.9, 0.88, segment.progress),
          heading: exitHeading,
          pitch: 0,
          roll: landingExitTurnSide * Math.sin(exitProgress * Math.PI) * 0.9,
          altitude: 0,
          speed: landingRunwayProfile.holdSpeed,
          thrust: 0,
          opacity: 1 - fadeOut * 0.92,
          lightOpacity: 0.06,
          heatOpacity: 0,
          shadowOpacity: lerp(0.34, 0.16, segment.progress),
          shadowBlur: 5,
          shadowStretch: 0.94,
        }),
        elapsed
      );
    }

    case 'transition':
      return DEFAULT_SCENE_STATE;

    case 'taxiIn': {
      const taxiProgress = segment.progress;
      const taxiT = getBezierTAtArcProgress(taxiInArcTable, taxiProgress);
      const taxiPoint = sampleBezierPoint(
        taxiInPath.p0,
        taxiInPath.p1,
        taxiInPath.p2,
        taxiInPath.p3,
        taxiT
      );
      const taxiTangent = sampleBezierTangent(
        taxiInPath.p0,
        taxiInPath.p1,
        taxiInPath.p2,
        taxiInPath.p3,
        taxiT
      );

      return addMotionTexture(
        composeSceneState(metrics, {
          phase: 'taxi-in',
          visible: true,
          x: taxiPoint.x,
          y: taxiPoint.y,
          baseScale: 0.92,
          heading: getHeadingFromVector(taxiTangent.x, taxiTangent.y, 0),
          pitch: 0,
          roll: 0,
          altitude: 0,
          speed: lerp(0.12, 0.08, taxiProgress),
          thrust: 0.16,
          opacity: segment.progress < 0.08 ? segment.progress / 0.08 : 1,
          lightOpacity: 0.42,
          heatOpacity: 0.04,
          shadowOpacity: 0.38,
          shadowBlur: 4,
          shadowStretch: 0.96,
        }),
        elapsed
      );
    }

    case 'lineUp': {
      const settle = segment.progress;

      return addMotionTexture(
        composeSceneState(metrics, {
          phase: 'line-up',
          visible: true,
          x: centerX,
          y: lerp(taxiEntryY, takeoffStartY, settle),
          baseScale: 0.92,
          heading: 0,
          pitch: 0,
          roll: 0,
          altitude: 0,
          speed: lerp(0.08, takeoffRunwayProfile.startSpeed ?? 0.04, segment.progress),
          thrust: lerp(0.16, 0.32, segment.progress),
          lightOpacity: 0.72,
          heatOpacity: 0.12,
          shadowOpacity: 0.38,
          shadowBlur: 4,
          shadowStretch: 0.96,
        }),
        elapsed
      );
    }

    case 'takeoffRoll': {
      return addMotionTexture(
        composeSceneState(metrics, {
          phase: 'takeoff-roll',
          visible: true,
          x: centerX,
          y: takeoffRunwayY,
          baseScale: 0.92,
          heading: 0,
          pitch: segment.progress < 0.35 ? lerp(0, 0.6, segment.progress / 0.35) : 0.6,
          roll: 0,
          altitude: 0,
          speed: takeoffRunwayProfile.speed,
          thrust: lerp(0.28, 1, segment.progress),
          lightOpacity: lerp(0.72, 1, segment.progress),
          heatOpacity: lerp(0.12, 0.46, segment.progress),
          shadowOpacity: 0.4,
          shadowBlur: 4,
          shadowStretch: lerp(0.96, 1.02, segment.progress),
        }),
        elapsed
      );
    }

    case 'rotate': {
      const rotateProgress = easeOutCubic(segment.progress);

      return addMotionTexture(
        composeSceneState(metrics, {
          phase: 'rotate',
          visible: true,
          x: centerX,
          y: takeoffRunwayY,
          baseScale: lerp(0.92, 0.9, segment.progress),
          heading: 0,
          pitch: lerp(0.6, 13.6, rotateProgress),
          roll: 0,
          altitude: lerp(0, 0.42, easeInCubic(segment.progress)),
          speed: takeoffRunwayProfile.speed,
          thrust: lerp(1, 0.86, segment.progress),
          lightOpacity: 1,
          heatOpacity: lerp(0.46, 0.34, segment.progress),
          shadowOpacity: 0.38,
          shadowBlur: lerp(4, 8, segment.progress),
          shadowStretch: lerp(1.02, 1.08, segment.progress),
        }),
        elapsed
      );
    }

    case 'climb': {
      return addMotionTexture(
        composeSceneState(metrics, {
          phase: 'climb',
          visible: true,
          x: centerX,
          y: takeoffRunwayY,
          baseScale: lerp(0.9, 0.8, segment.progress),
          heading: 0,
          pitch: lerp(13.6, 10.4, segment.progress),
          roll: 0,
          altitude: lerp(0.42, 0.9, easeInOutCubic(segment.progress)),
          speed: takeoffRunwayProfile.speed,
          thrust: lerp(0.86, 0.6, segment.progress),
          lightOpacity: lerp(1, 0.7, segment.progress),
          heatOpacity: lerp(0.34, 0.18, segment.progress),
          shadowOpacity: lerp(0.28, 0.12, segment.progress),
          shadowBlur: lerp(8, 14, segment.progress),
          shadowStretch: lerp(1.08, 1.16, segment.progress),
        }),
        elapsed
      );
    }

    case 'departure': {
      const departureProgress = segment.progress;
      const departureT = getBezierTAtArcProgress(departureArcTable, departureProgress);
      const fadeProgress = clamp((segment.progress - 0.72) / 0.28, 0, 1);
      const departurePoint = sampleBezierPoint(
        departurePath.p0,
        departurePath.p1,
        departurePath.p2,
        departurePath.p3,
        departureT
      );
      const departureTangent = sampleBezierTangent(
        departurePath.p0,
        departurePath.p1,
        departurePath.p2,
        departurePath.p3,
        departureT
      );

      return addMotionTexture(
        composeSceneState(metrics, {
          phase: 'departure',
          visible: true,
          x: departurePoint.x,
          y: departurePoint.y,
          baseScale: lerp(0.8, 0.72, segment.progress),
          heading: getHeadingFromVector(departureTangent.x, departureTangent.y, 0),
          pitch: lerp(10.2, 7.2, segment.progress),
          roll: departureSide * Math.sin(segment.progress * Math.PI * 0.8) * 1.6,
          altitude: lerp(0.9, 1.18, departureProgress),
          speed: lerp(
            takeoffRunwayProfile.maxSpeed,
            takeoffRunwayProfile.maxSpeed * 0.88,
            departureProgress
          ),
          thrust: lerp(0.6, 0.42, segment.progress),
          opacity: 1 - fadeProgress,
          lightOpacity: lerp(0.7, 0.3, segment.progress),
          heatOpacity: lerp(0.18, 0.06, segment.progress),
          shadowOpacity: lerp(0.12, 0.05, segment.progress),
          shadowBlur: lerp(14, 18, segment.progress),
          shadowStretch: lerp(1.16, 1.22, segment.progress),
        }),
        elapsed
      );
    }

    case 'reset':
    default:
      return DEFAULT_SCENE_STATE;
  }
};

const applySceneState = (element, state) => {
  if (!element) {
    return;
  }

  const formatStyleValue = (value, digits = 4) => value.toFixed(digits);
  const airborne = state.altitude > 0.02;

  element.dataset.phase = state.phase;
  element.dataset.airborne = airborne ? 'true' : 'false';

  element.style.setProperty('--aircraft-x', `${formatStyleValue(state.x)}px`);
  element.style.setProperty('--aircraft-y', `${formatStyleValue(state.y)}px`);
  element.style.setProperty('--aircraft-scale', formatStyleValue(state.scale));
  element.style.setProperty('--aircraft-heading', `${formatStyleValue(state.heading)}deg`);
  element.style.setProperty('--aircraft-pitch', `${formatStyleValue(state.pitch)}deg`);
  element.style.setProperty('--aircraft-roll', `${formatStyleValue(state.roll)}deg`);
  element.style.setProperty('--aircraft-opacity', formatStyleValue(state.opacity));
  element.style.setProperty('--aircraft-body-x', `${formatStyleValue(state.bodyX)}px`);
  element.style.setProperty('--aircraft-body-y', `${formatStyleValue(state.bodyY)}px`);
  element.style.setProperty('--aircraft-thrust', formatStyleValue(state.thrust));
  element.style.setProperty('--aircraft-light-opacity', formatStyleValue(state.lightOpacity));
  element.style.setProperty('--aircraft-heat-opacity', formatStyleValue(state.heatOpacity));
  element.style.setProperty('--shadow-x', `${formatStyleValue(state.shadowX)}px`);
  element.style.setProperty('--shadow-y', `${formatStyleValue(state.shadowY)}px`);
  element.style.setProperty('--shadow-scale', formatStyleValue(state.shadowScale));
  element.style.setProperty('--shadow-opacity', formatStyleValue(state.shadowOpacity));
  element.style.setProperty('--shadow-blur', `${formatStyleValue(state.shadowBlur)}px`);
  element.style.setProperty('--shadow-stretch', formatStyleValue(state.shadowStretch));
  element.style.setProperty('--smoke-x', `${formatStyleValue(state.smokeX)}px`);
  element.style.setProperty('--smoke-y', `${formatStyleValue(state.smokeY)}px`);
  element.style.setProperty('--smoke-scale', formatStyleValue(state.smokeScale));
  element.style.setProperty('--smoke-opacity', formatStyleValue(state.smokeOpacity));
  element.style.setProperty('--smoke-rotation', `${formatStyleValue(state.smokeRotation)}deg`);
  element.style.setProperty('--smoke-stretch', formatStyleValue(state.smokeStretch));
};

const FallbackAircraft = () => (
  <svg
    width="64"
    height="64"
    viewBox="0 0 64 64"
    role="img"
    aria-label="Aircraft"
  >
    <path
      d="M32 5c4.3 0 6.4 4.4 6.4 12.8v12.3l12.3 8.6c2 1.4 2.8 4 2.2 6.3l-1 3.7a1.9 1.9 0 0 1-2.6 1.3L38.4 45v7.4l4.7 5.2c1 1.1 1.3 2.8.8 4.2l-.5 1.4a1.9 1.9 0 0 1-2.4 1.1L32 61.6l-9 2.7a1.9 1.9 0 0 1-2.4-1.1l-.5-1.4a4.3 4.3 0 0 1 .8-4.2l4.7-5.2V45L14.7 50a1.9 1.9 0 0 1-2.6-1.3l-1-3.7c-.6-2.3.2-4.9 2.2-6.3l12.3-8.6V17.8C25.6 9.4 27.7 5 32 5Z"
      fill="currentColor"
      opacity="0.96"
    />
  </svg>
);

const Runway = () => {
  const containerRef = useRef(null);
  const runwayRef = useRef(null);
  const frameRef = useRef(0);
  const lastFrameTimeRef = useRef(0);
  const elapsedRef = useRef(0);
  const metricsRef = useRef(null);
  const sceneGeometryRef = useRef({
    metrics: null,
    cycleIndex: null,
    geometry: null,
  });
  const inViewRef = useRef(true);
  const reducedMotionRef = useRef(false);
  const [hasImageError, setHasImageError] = useState(false);
  const numApproachRows = 12;
  const approachCrossbarRows = new Set([3, 6, 9]);
  const numThresholdLights = 8;
  const numCenterlineLights = 30;
  const touchdownZoneRows = 3;
  const numPapiLights = 4;
  const numRunwayEndLights = 8;
  const numEdgeLights = 22;
  const thresholdCenter = (numThresholdLights - 1) / 2;
  const edgeCenter = (numEdgeLights - 1) / 2;
  const endCenter = (numRunwayEndLights - 1) / 2;

  useEffect(() => {
    const container = containerRef.current;
    const runway = runwayRef.current;

    if (!container || !runway) {
      return undefined;
    }

    const mediaQuery =
      typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-reduced-motion: reduce)')
        : null;

    const measure = () => {
      metricsRef.current = measureRunwayScene(container, runway);
      sceneGeometryRef.current = {
        metrics: null,
        cycleIndex: null,
        geometry: null,
      };
    };

    const renderCurrentFrame = () => {
      if (!metricsRef.current) {
        return;
      }

      if (reducedMotionRef.current) {
        applySceneState(container, getParkedSceneState(metricsRef.current));
        return;
      }

      const cycleIndex = Math.floor(elapsedRef.current / TOTAL_MOTION_DURATION);
      const phaseElapsed = elapsedRef.current % TOTAL_MOTION_DURATION;
      const cachedScene = sceneGeometryRef.current;

      if (
        cachedScene.metrics !== metricsRef.current ||
        cachedScene.cycleIndex !== cycleIndex
      ) {
        sceneGeometryRef.current = {
          metrics: metricsRef.current,
          cycleIndex,
          geometry: buildRunwaySceneGeometry(metricsRef.current, cycleIndex),
        };
      }

      applySceneState(
        container,
        getRunwaySceneState(
          phaseElapsed,
          metricsRef.current,
          sceneGeometryRef.current.geometry
        )
      );
    };

    const stopAnimation = () => {
      if (frameRef.current) {
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = 0;
      }
      lastFrameTimeRef.current = 0;
    };

    const shouldAnimate = () =>
      !reducedMotionRef.current && !document.hidden && inViewRef.current;

    const tick = (now) => {
      if (!lastFrameTimeRef.current) {
        lastFrameTimeRef.current = now;
      }

      const dt = Math.min(now - lastFrameTimeRef.current, 48);
      lastFrameTimeRef.current = now;
      elapsedRef.current += dt;
      renderCurrentFrame();
      frameRef.current = window.requestAnimationFrame(tick);
    };

    const startAnimation = () => {
      if (frameRef.current || !shouldAnimate()) {
        return;
      }

      lastFrameTimeRef.current = performance.now();
      frameRef.current = window.requestAnimationFrame(tick);
    };

    const syncAnimationState = () => {
      if (shouldAnimate()) {
        startAnimation();
      } else {
        stopAnimation();
        renderCurrentFrame();
      }
    };

    const handleVisibilityChange = () => {
      syncAnimationState();
    };

    const handleMotionPreference = (event) => {
      reducedMotionRef.current = event.matches;
      syncAnimationState();
    };

    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => {
        measure();
        renderCurrentFrame();
      }) : null;

    if (resizeObserver) {
      resizeObserver.observe(container);
      resizeObserver.observe(runway);
    }

    const intersectionObserver =
      typeof IntersectionObserver !== 'undefined'
        ? new IntersectionObserver(
            (entries) => {
              const entry = entries[0];
              inViewRef.current =
                Boolean(entry?.isIntersecting) || (entry?.intersectionRatio ?? 0) > 0.12;
              syncAnimationState();
            },
            { threshold: [0, 0.12, 0.25] }
          )
        : null;

    if (intersectionObserver) {
      intersectionObserver.observe(container);
    }

    window.addEventListener('resize', measure);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    reducedMotionRef.current = Boolean(mediaQuery?.matches);

    if (mediaQuery) {
      if (typeof mediaQuery.addEventListener === 'function') {
        mediaQuery.addEventListener('change', handleMotionPreference);
      } else if (typeof mediaQuery.addListener === 'function') {
        mediaQuery.addListener(handleMotionPreference);
      }
    }

    measure();
    renderCurrentFrame();
    syncAnimationState();

    return () => {
      stopAnimation();
      window.removeEventListener('resize', measure);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (intersectionObserver) {
        intersectionObserver.disconnect();
      }
      if (mediaQuery) {
        if (typeof mediaQuery.removeEventListener === 'function') {
          mediaQuery.removeEventListener('change', handleMotionPreference);
        } else if (typeof mediaQuery.removeListener === 'function') {
          mediaQuery.removeListener(handleMotionPreference);
        }
      }
    };
  }, []);

  return (
    <div className="runway-container" ref={containerRef}>
      <div className="runway-traffic-scene" aria-hidden="true">
        <div className="runway-aircraft-shadow"></div>
        <div className="runway-aircraft-smoke"></div>
        <div className="runway-aircraft">
          <div className="runway-aircraft__body">
            <span className="runway-aircraft__heat"></span>
            <span className="runway-aircraft__landing-light"></span>
            <span className="runway-aircraft__beacon"></span>
            <span className="runway-aircraft__nav-light runway-aircraft__nav-light--port"></span>
            <span className="runway-aircraft__nav-light runway-aircraft__nav-light--starboard"></span>
            {hasImageError ? (
              <FallbackAircraft />
            ) : (
              <img
                src="/images/airplane.png"
                alt=""
                loading="lazy"
                decoding="async"
                onError={() => setHasImageError(true)}
              />
            )}
          </div>
        </div>
      </div>

      <div className="approaching-lights-wrapper">
        {Array.from({ length: numApproachRows }).map((_, i) => {
          const showCrossbar = approachCrossbarRows.has(i);
          return (
            <div key={`approach-row-${i}`} className="approach-row" style={{ '--i': i }}>
              <div
                className="approach-light approach-center"
                style={{ animationDelay: `${i * 0.12}s` }}
              ></div>
              {showCrossbar ? (
                <div className="approach-crossbar">
                  <div className="approach-side-group">
                    <div className="approach-light approach-side"></div>
                    <div className="approach-light approach-side"></div>
                  </div>
                  <div className="approach-side-group">
                    <div className="approach-light approach-side"></div>
                    <div className="approach-light approach-side"></div>
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="runway" ref={runwayRef}>
        <div className="threshold-markings">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={`threshold-mark-${i}`} className="threshold-stripe"></div>
          ))}
        </div>

        <div className="threshold-lights-wrapper">
          {Array.from({ length: numThresholdLights }).map((_, i) => {
            const delay = Math.abs(i - thresholdCenter) * 0.12;
            return (
              <div
                key={`threshold-${i}`}
                className="threshold-light"
                style={{ '--delay': `${delay}s` }}
              ></div>
            );
          })}
        </div>

        <div className="runway-designator">
          <span className="runway-number">36</span>
        </div>

        <div className="papi-lights papi-left" aria-hidden="true">
          {Array.from({ length: numPapiLights }).map((_, i) => (
            <span
              key={`papi-${i}`}
              className={`papi-light ${i < 2 ? 'papi-white' : 'papi-red'}`}
              style={{ animationDelay: `${i * 0.2}s` }}
            ></span>
          ))}
        </div>
        <div className="papi-lights papi-right" aria-hidden="true">
          {Array.from({ length: numPapiLights }).map((_, i) => (
            <span
              key={`papi-r-${i}`}
              className={`papi-light ${i < 2 ? 'papi-white' : 'papi-red'}`}
              style={{ animationDelay: `${i * 0.2}s` }}
            ></span>
          ))}
        </div>

        <div className="touchdown-zone-lights" aria-hidden="true">
          {Array.from({ length: touchdownZoneRows }).map((_, i) => (
            <div key={`tdz-row-${i}`} className="touchdown-row" style={{ '--delay': `${i * 0.18}s` }}>
              <span className="tdz-light"></span>
              <span className="tdz-light"></span>
            </div>
          ))}
        </div>

        <div className="runway-centerline-lights" aria-hidden="true">
          {Array.from({ length: numCenterlineLights }).map((_, i) => {
            const ratio = i / (numCenterlineLights - 1);
            const stage =
              ratio > 0.85
                ? 'centerline-red'
                : ratio > 0.65
                  ? 'centerline-amber'
                  : 'centerline-white';
            return (
              <span
                key={`centerline-${i}`}
                className={`centerline-light ${stage}`}
                style={{ '--delay': `${i * 0.08}s`, '--ratio': ratio.toFixed(4) }}
              ></span>
            );
          })}
        </div>

        <div className="runway-edge-lights-left">
          {Array.from({ length: numEdgeLights }).map((_, i) => {
            const ratio = i / (numEdgeLights - 1);
            const delay = Math.abs(i - edgeCenter) * 0.1;
            return (
              <div
                key={`edge-left-${i}`}
                className="runway-edge-light"
                style={{ '--delay': `${delay}s`, '--ratio': ratio.toFixed(4) }}
              ></div>
            );
          })}
        </div>
        <div className="runway-edge-lights-right">
          {Array.from({ length: numEdgeLights }).map((_, i) => {
            const ratio = i / (numEdgeLights - 1);
            const delay = Math.abs(i - edgeCenter) * 0.1;
            return (
              <div
                key={`edge-right-${i}`}
                className="runway-edge-light"
                style={{ '--delay': `${delay}s`, '--ratio': ratio.toFixed(4) }}
              ></div>
            );
          })}
        </div>

        <div className="runway-end-lights-wrapper">
          {Array.from({ length: numRunwayEndLights }).map((_, i) => {
            const delay = Math.abs(i - endCenter) * 0.12;
            return (
              <div
                key={`end-${i}`}
                className="runway-end-light"
                style={{ '--delay': `${delay}s` }}
              ></div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Runway;
